import { useState, useRef, useEffect } from 'react';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { UI_LANGUAGES } from '@/lib/ui-languages';
import type { Language as AppLanguage } from '@/stores/language.store';

interface HeaderLanguageSelectorProps {
  className?: string;
  buttonClassName?: string;
}

export function HeaderLanguageSelector({ className = '', buttonClassName = '' }: HeaderLanguageSelectorProps) {
  const { language, changeLanguage } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (lang: AppLanguage) => {
    changeLanguage(lang);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-center px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 group text-[#5C5C5C] hover:text-[#001B3D] hover:bg-[#FDFBF7] ${buttonClassName}`}
        aria-label="Sélectionner la langue"
      >
        <Globe className="h-4 w-4 text-[#5C5C5C] group-hover:text-[#B38E5D] transition-colors" />
      </button>

      {isOpen && (
        <div className="absolute end-0 z-50 mt-2 w-52 rounded-2xl border border-[#E5DDD2] bg-white py-2 shadow-xl backdrop-blur-lg">
          {UI_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleLanguageSelect(lang.code as AppLanguage)}
              className={`flex w-full items-center justify-between px-4 py-2 text-start text-sm transition-colors ${
                language === lang.code
                  ? 'bg-[#F0EBE3] text-[#B38E5D]'
                  : 'text-[#5C5C5C] hover:bg-[#FDFBF7] hover:text-[#001B3D]'
              }`}
            >
              <span lang={lang.code}>{`${lang.flag} ${lang.label}`}</span>
              {language === lang.code && <Check className="h-4 w-4 shrink-0 text-[#B38E5D]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
